import { memo, useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { getSecurityKey } from '../../../../util/security'
import { calculateOptionOutlook } from '../../../../util/options'
import { Outlook, outlookToJSON } from '../../../../proto/generated/candle-data-depot-option-chain'
import { RootState } from '../../../../store'
import { getOutlookColorClassName, OptionSecurity, OutlookToName } from '../../../typings/options.typings'
import '../../../../components/StrikeTooltip/style.css'

type OptionChainOutlookTagProps = {
    obj: OptionSecurity
}

/** Short outlook tag only (e.g. LB, SB) — no full outlook name. */
const OptionChainOutlookTag = ({ obj }: OptionChainOutlookTagProps) => {
    const store = useStore<RootState>()
    const tagRef = useRef<HTMLSpanElement>(null)
    const secKey = getSecurityKey(obj?.security?.exchange ?? 0, obj?.security?.code ?? '')
    const initialOiChange = Number(obj?.oiChanges?.oiChange?.change ?? 0)
    const initialLtpChangePercent = Number(obj?.ltpChangePercent ?? 0)
    const initialOutlook = obj?.oiChanges?.outlook

    useEffect(() => {
        const apply = () => {
            const tagEl = tagRef.current
            if (!tagEl) return

            const row = store.getState().securityStore.candleChangeBook?.[secKey]
            const hasLive = row?.oiChange !== undefined || row?.ltpChangePercent !== undefined
            const oiChange = hasLive ? Number(row?.oiChange ?? 0) : initialOiChange
            const ltpChangePercent = hasLive ? Number(row?.ltpChangePercent ?? 0) : initialLtpChangePercent
            const outlook = hasLive
                ? calculateOptionOutlook(ltpChangePercent, oiChange)
                : initialOutlook

            if ([Outlook.OUTLOOK_UNKNOWN, Outlook.UNRECOGNIZED, undefined].includes(outlook)) {
                tagEl.textContent = ''
                tagEl.style.display = 'none'
                return
            }

            const outlookKey = outlookToJSON(Number(outlook))
            const tag = OutlookToName[outlookKey]
            const colorClass = getOutlookColorClassName(tag)

            tagEl.textContent = tag
            tagEl.style.display = tag ? 'inline-flex' : 'none'
            tagEl.className = `strike-tooltip-outlook-tag option-chain-outlook-tag ${colorClass}`.trim()
        }
        apply()
        return store.subscribe(apply)
    }, [store, secKey, initialOiChange, initialLtpChangePercent, initialOutlook])

    return (
        <span
            ref={tagRef}
            className="strike-tooltip-outlook-tag option-chain-outlook-tag"
            style={{ display: 'none', pointerEvents: 'none' }}
        />
    )
}

export default memo(OptionChainOutlookTag)
